import { useState } from "react";
import { Film } from "lucide-react";

/**
 * ReelThumb — renders the first frame of the reel's video as a thumbnail,
 * directly from the stored video (Emergent Object Store, S3, YouTube, any
 * MP4/WebM URL). No separate thumbnail service required.
 *
 * Priority:
 *   1. If `reel.thumbnail_url` is set and loads → show it
 *   2. Else if `reel.video_url` is set → render a muted <video preload=metadata>
 *      which paints the first frame natively
 *   3. Else → Film-icon placeholder
 *
 * Backend path routing: Emergent Object Store videos come back as
 * `/api/files/<...>`. We prefix REACT_APP_BACKEND_URL automatically for those.
 */
export default function ReelThumb({ reel, className = "w-14 h-20", iconSize = "w-5 h-5" }) {
  const [imgErr, setImgErr] = useState(false);
  const [vidErr, setVidErr] = useState(false);

  const thumbUrl = reel?.thumbnail_url || reel?.thumbnail || reel?.cover_url || null;
  const rawVideo = reel?.video_url || reel?.url || null;
  const videoUrl = (() => {
    if (!rawVideo) return null;
    if (rawVideo.startsWith("/api/")) {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      return `${base}${rawVideo}`;
    }
    return rawVideo;
  })();

  return (
    <div className={`relative ${className} rounded-md bg-white/10 border border-white/15 flex-shrink-0 overflow-hidden`}>
      {thumbUrl && !imgErr ? (
        <img src={thumbUrl} alt="" onError={() => setImgErr(true)}
          className="absolute inset-0 w-full h-full object-cover"/>
      ) : videoUrl && !vidErr ? (
        <video
          src={`${videoUrl}${videoUrl.includes("#") ? "" : "#t=0.1"}`}
          preload="metadata"
          muted
          playsInline
          onError={() => setVidErr(true)}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <Film className={`${iconSize} text-white/55`}/>
        </div>
      )}
    </div>
  );
}
